import { createContext, useState, useMemo } from "react";
import PropTypes from "prop-types";
import useSessionStorage from "../hooks/useSessionStorage";
import { jwtDecode } from "jwt-decode";
import useRole from "../hooks/useRole";

export const CUSTOMERTYPE = {
    PERSON: 'person',
    COMPANY: 'company'
};

const context = {
    authStatus: null,
    authRoles: [],
    authCustomerType: null,
    authBusinessId: '',
    authCompany: '',
    authName: '',
    authLoggedIn: false,
    authUserHasAccount: false,
    authChosenCustomerType: null,
    authSetChosenCustomerType: () => {},
    authInitialize: () => {},
    authSignOut: () => {}
}

export const AuthContext = createContext(context);

const AuthContextProvider = ({ children }) => {
    const { roleSolver } = useRole();
    const {
        userSetAuthStatus,
        userSetRoles,
        userSetBusinessId,
        userSetName,
        userSetToken,
        userSetCustomerType,
        userSetCompany,
        userGetChosenCustomerType,
        userGetToken,
        userSetChosenCustomerType,
        clearSessionStorage
    } = useSessionStorage();

    const token = userGetToken();
    const decodedToken = token !== null && token !== '' ? jwtDecode(token) : null;

    const [tokenDecoded, setTokenDecoded] = useState(decodedToken);
    const [chosenCustomerType, setChosenCustomerType] = useState(userGetChosenCustomerType());

    const authSignOut = () => {
        setTokenDecoded(null);
        clearSessionStorage();
    }

    /**
     * Initialize user session with token received from the back-end
     * @param {string} token
     * @returns {void}
     */
    const authInitialize = (token) => {
        let decoded = null;

        try {
            decoded = jwtDecode(token);
        } catch(err) {
            return;
        }

        if (decoded !== null) {
            setTokenDecoded(decoded);
            userSetToken(token);
            // TODO: Get rid these session storage setters (token should be the only one)
            userSetRoles(roleSolver(decoded.roles));
            userSetName(decoded.name ?? '');
            userSetAuthStatus(decoded.authstatus ?? null);
            // When customertype is company, company name and businessId are also available
            if (decoded.customertype === CUSTOMERTYPE.COMPANY) {
                userSetCustomerType(CUSTOMERTYPE.COMPANY);
                userSetCompany(decoded.company ?? '');
                userSetBusinessId(decoded.businessid ?? '');
            } else {
                userSetCustomerType(CUSTOMERTYPE.PERSON);
            }
        }
    }

    /**
     * Set chosen customer type to session storage and user session.
     * @param {string|null} type
     */
    const authSetChosenCustomerType = (type) => {
        userSetChosenCustomerType(type);
        setChosenCustomerType(type);
    }

    const providerValue = useMemo(() => (
        {
            authStatus: tokenDecoded?.authstatus ?? null,
            authRoles: roleSolver(tokenDecoded?.roles),
            authCustomerType: tokenDecoded?.customertype ?? null,
            authBusinessId: tokenDecoded?.businessid ?? '',
            authCompany: tokenDecoded?.company ?? '',
            authName: tokenDecoded?.name ?? '',
            authLoggedIn: userGetToken() !== null,
            authUserHasAccount: tokenDecoded?.roles?.includes('userpool'),
            authChosenCustomerType: chosenCustomerType,
            authSetChosenCustomerType,
            authInitialize,
            authSignOut
        }
    ), [
        authInitialize, authSignOut, tokenDecoded, chosenCustomerType, setChosenCustomerType, roleSolver
    ]);

    return (
        <AuthContext.Provider value={providerValue}>
            {children}
        </AuthContext.Provider>
    )
}

AuthContextProvider.propTypes = {
    children: PropTypes.node.isRequired
};

export default AuthContextProvider;
